import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { PRODUCT_FILTER_OPTIONS, type ProductFilterLabels } from "@/types/types";
import { filterOffersChoice } from "@/utils/redemption-helper";
import FilterChip from "./FilterChip";
import FilterDropdown from "./FilterDropdown";

export interface ProductFilterBarProps {
  displayFilter: ProductFilterLabels;
  onDisplayFilterChange: (value: ProductFilterLabels) => void;
  /** GFG gift categories on offer (see availableGiftCategories). */
  categories: string[];
  /** Categories currently picked (multi-select); empty = all. */
  activeCats: string[];
  /** Toggle one category on/off. */
  onChooseCat: (category: string) => void;
  /** Clear every category pick. */
  onClearCats: () => void;
  causes: string[];
  /** Causes currently picked (multi-select); empty = all. */
  activeCauses: string[];
  /** Toggle one cause on/off. */
  onChooseCause: (value: string) => void;
  /** Clear every cause pick. */
  onClearCauses: () => void;
  /** "Ships to" country — filters physical gifts by shippability (charity/experience unaffected). */
  selectedCountry: string;
  onCountryChange: (code: string) => void;
}

// Detail-view product filter bar: display-type chips (Physical Gifts · Charity Gifts ·
// Experiences) + multi-select Category / Cause dropdowns (button shows the pick count) + a country
// ("Ships to") picker. The type chips are a single-select coarse filter that scopes + prunes the
// Category/Cause picks (see useProductFilters). Shared by SingleCollection + CollectionTier.
export default function ProductFilterBar({
  displayFilter,
  onDisplayFilterChange,
  categories,
  activeCats,
  onChooseCat,
  onClearCats,
  causes,
  activeCauses,
  onChooseCause,
  onClearCauses,
  selectedCountry,
  onCountryChange,
}: ProductFilterBarProps) {
  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        p: 2,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mb: 2,
      }}
    >
      {/* Left — display-type chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--color-primary-active)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Filter:
          </Typography>
          <Box
            data-testid="product-filter-chips"
            sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}
          >
            {PRODUCT_FILTER_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={displayFilter === opt.value}
                onClick={() =>
                  onDisplayFilterChange(displayFilter === opt.value ? "all" : opt.value)
                }
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right — country ("Ships to") + Category / Cause single-selects */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, flexWrap: "wrap" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--color-dark-dust)",
              whiteSpace: "nowrap",
            }}
          >
            Ships to:
          </Typography>
          <CountryDropdown selectedCountry={selectedCountry} onChange={onCountryChange} />
        </Box>
        {/* Multi-select: options are the display labels; onToggle maps the label back to its raw
            category and toggles it (the button shows the pick count; mirrors RedemptionFilterBar).
            Each renders only when it offers a real choice (≥2 options) — on the Charity/Experiences
            type the lone category is already implied by the active chip. */}
        {filterOffersChoice(categories) && (
          <FilterDropdown
            label="Category"
            options={categories}
            selected={activeCats}
            onToggle={onChooseCat}
            onClear={onClearCats}
          />
        )}
        {filterOffersChoice(causes) && (
          <FilterDropdown
            label="Cause"
            options={causes}
            selected={activeCauses}
            onToggle={onChooseCause}
            onClear={onClearCauses}
          />
        )}
      </Box>
    </Box>
  );
}
